import { type PluginListenerHandle, registerPlugin } from "@capacitor/core"
import { Preferences } from "@capacitor/preferences"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type NativeSyncReason =
  | "foreground"
  | "background_fetch"
  | "silent_push"
  | "boot"
  | "manual"
  | "startup"
  | string

export interface NativeBridgeConfigureOptions {
  apiBaseUrl: string
  dexieCloudDbUrl?: string
  /** Enables native debug logging + remote log-dump posting. Default: false */
  debugLoggingEnabled?: boolean
}

export interface NativeBridgeRunSyncOptions {
  reason: NativeSyncReason
}

export interface NativeBridgeRunSyncResult {
  ok: boolean
  skipped?: boolean
  error?: string
  reason?: string
  scheduled: number
  cancelled: number
  totalDesired: number
  totalScheduledAfter: number
  didRefreshDexieToken?: boolean
}

export interface NativeBridgeScheduleLocalNotificationOptions {
  reminderId: string
  taskId?: string
  parentPath: string
  kind: string
  title?: string
  body?: string
  link?: string
  fireAtMs: number | string
}

export interface NativeBridgeScheduleLocalNotificationResult {
  ok: boolean
  scheduled: boolean
  instanceKey: string
  path?: "alarmkit" | "un" | "un_fallback"
  error?: string
}

export interface NativeBridgeCancelLocalNotificationOptions {
  reminderId: string
  kind: string
  title?: string
  body?: string
  link?: string
  fireAtMs: number | string
  parentPath: string
}

export interface NativeBridgeCancelLocalNotificationResult {
  ok: boolean
  cancelled: boolean
  instanceKey: string
}

export type NativeBridgePermissionStatus = "granted" | "denied" | "unknown"

export interface NativeBridgeNotificationPermissionResult {
  status: NativeBridgePermissionStatus
}

export interface NativeBridgeAlarmPermissionResult {
  status: NativeBridgePermissionStatus
  opened?: boolean
  reason?: string
}

export type NativeBridgeBackgroundRefreshStatus =
  | "available"
  | "denied"
  | "restricted"
  | "unknown"

export interface NativeBridgeBackgroundExecutionStatus {
  platform: "android" | "ios" | string

  notificationsEnabled?: boolean

  // Android
  androidIsIgnoringBatteryOptimizations?: boolean
  androidIsBackgroundRestricted?: boolean
  androidCanScheduleExactAlarms?: boolean

  // iOS
  iosBackgroundRefreshStatus?: NativeBridgeBackgroundRefreshStatus
}

export interface NativeBridgeOpenSettingsResult {
  opened: boolean
  reason?: string
}

export interface NativeBridgeStableDeviceIdResult {
  stableDeviceId: string
  source?: string
}

export interface NativeBridgeStatus {
  lockUntilMs?: number
  lastRunAtMs?: number
  lastOk?: boolean
  lastError?: string

  apiBaseUrl?: string
  dexieCloudDbUrl?: string

  dexieTokenExpiresAtMs?: number
  dexieTokenSecondsLeft?: number
  dexieTokenIsExpired?: boolean
}

export interface NativeBridgeDiagnostics extends NativeBridgeStatus {
  pendingCount: number
  deliveredCount: number
  maxPendingUNNotifications: number
  headroom: number
  atCapacity: boolean
  pendingReminders: number
  pendingAlarms: number
  pendingOther: number
  cachedReminderKeys: number
  cachedAlarmKeys: number
  cachedLegacyKeys: number
  usesAlarmKit: boolean
  authorizationStatus: string
  timeSensitive: boolean
  earliestPendingMs?: number
  latestPendingMs?: number
}

export interface NativeBridgePlugin {
  configure(options: NativeBridgeConfigureOptions): Promise<void>
  runSyncNow(
    options: NativeBridgeRunSyncOptions,
  ): Promise<NativeBridgeRunSyncResult>
  getStableDeviceId(): Promise<NativeBridgeStableDeviceIdResult>
  scheduleLocalNotification(
    options: NativeBridgeScheduleLocalNotificationOptions,
  ): Promise<NativeBridgeScheduleLocalNotificationResult>
  cancelLocalNotification(
    options: NativeBridgeCancelLocalNotificationOptions,
  ): Promise<NativeBridgeCancelLocalNotificationResult>
  getStatus(): Promise<NativeBridgeStatus>

  /** Rich diagnostics for the ReminderDiagnostics settings panel. */
  getDiagnostics(): Promise<NativeBridgeDiagnostics>
  /**
   * iOS: restores the web view's height when a keyboard vanished without a hide event (KeyboardRecovery
   * in NativeBridgePlugin.swift) and fires keyboardWillHide / keyboardDidHide. Not implemented on Android.
   */
  repairKeyboard(): Promise<{ repaired: boolean }>

  getNotificationPermissionStatus(): Promise<NativeBridgeNotificationPermissionResult>
  requestNotificationPermission(): Promise<NativeBridgeNotificationPermissionResult>

  // Passive (no prompt) check for alarm authorization.
  getAlarmPermissionStatus(): Promise<NativeBridgeAlarmPermissionResult>
  requestAlarmPermission(): Promise<NativeBridgeAlarmPermissionResult>

  getBackgroundExecutionStatus(): Promise<NativeBridgeBackgroundExecutionStatus>
  openBackgroundExecutionSettings(): Promise<NativeBridgeOpenSettingsResult>
  requestIgnoreBatteryOptimizations(): Promise<NativeBridgeOpenSettingsResult>

  addListener(
    eventName: "syncStarted" | "syncFinished" | "syncFailed",
    listenerFunc: (event: unknown) => void,
  ): Promise<PluginListenerHandle>
}

// ---------------------------------------------------------------------------
// Plugin registration — native implementations are loaded automatically
// on iOS/Android. Web fallback provides safe no-op stubs.
// ---------------------------------------------------------------------------

export const NativeBridge = registerPlugin<NativeBridgePlugin>("NativeBridge", {
  web: () => {
    const stableKey = "totodo_stable_device_id_v1"

    return {
      configure: async () => {
        // no-op on web
      },
      getStableDeviceId: async () => {
        try {
          const existing = await Preferences.get({ key: stableKey })
          if (existing?.value) {
            return { stableDeviceId: existing.value, source: "preferences" }
          }

          const next = crypto.randomUUID()
          await Preferences.set({ key: stableKey, value: next })
          return { stableDeviceId: next, source: "preferences" }
        } catch {
          // If storage is blocked (privacy mode), fall back to ephemeral.
          return { stableDeviceId: crypto.randomUUID(), source: "ephemeral" }
        }
      },
      runSyncNow: async () => ({
        ok: false,
        skipped: true,
        error: "NativeBridge is not available on web",
        scheduled: 0,
        cancelled: 0,
        totalDesired: 0,
        totalScheduledAfter: 0,
      }),
      scheduleLocalNotification: async () => ({
        ok: false,
        scheduled: false,
        instanceKey: "",
      }),
      cancelLocalNotification: async () => ({
        ok: false,
        cancelled: false,
        instanceKey: "",
      }),
      getStatus: async () => ({}),
      getDiagnostics: async () => ({
        pendingCount: 0,
        deliveredCount: 0,
        maxPendingUNNotifications: 64,
        headroom: 64,
        atCapacity: false,
        pendingReminders: 0,
        pendingAlarms: 0,
        pendingOther: 0,
        cachedReminderKeys: 0,
        cachedAlarmKeys: 0,
        cachedLegacyKeys: 0,
        usesAlarmKit: false,
        authorizationStatus: "unknown",
        timeSensitive: false,
      }),
      getNotificationPermissionStatus: async () => ({
        status: "unknown" as const,
      }),
      requestNotificationPermission: async () => ({
        status: "unknown" as const,
      }),
      getAlarmPermissionStatus: async () => ({
        status: "unknown" as const,
        opened: false,
        reason: "unavailable",
      }),
      requestAlarmPermission: async () => ({
        status: "unknown" as const,
        opened: false,
        reason: "unavailable",
      }),
      repairKeyboard: async () => ({ repaired: false }),
      getBackgroundExecutionStatus: async () => ({ platform: "web" }),
      openBackgroundExecutionSettings: async () => ({
        opened: false,
        reason: "unavailable",
      }),
      requestIgnoreBatteryOptimizations: async () => ({
        opened: false,
        reason: "unavailable",
      }),
      addListener: async () => {
        return {
          remove: async () => {
            // no-op
          },
        }
      },
    }
  },
})
